<!-- ModuleMenu.svelte
     Renders every module the sidebar exposes as tap-friendly cards on the main
     screen. On mobile this IS the navigation (the off-canvas sidebar is gone),
     so it also surfaces connection status and the Settings/Help/Logout actions
     that otherwise live in the sidebar. Fetches its own connection status so it
     can disable modules that aren't ready (same rules as Layout.svelte). -->
<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { user } from '../stores/authStore';
  import { settingsStore } from '../stores/settingsStore.js';
  import Icon from './Icon.svelte';
  import { isXeroReauthReason } from '../lib/xeroReauth.js';

  let userInfo = null;
  let jobtreadConnected = false;
  let xeroAuthenticated = false;
  let xeroNeedsFix = false;
  let xeroSettingsSaved = false;
  let loading = true;

  // Hide the Direct Bill Import module for specific users (mirrors Layout.svelte)
  const DIRECT_BILL_HIDDEN_USERS = ['tester@elevate.net.nz'];
  $: hideDirectBill = DIRECT_BILL_HIDDEN_USERS.includes(
    ($user?.email || $user?.username || '').trim().toLowerCase()
  );

  $: isQuickBooks = userInfo?.accountingPlatform === 'quickbooks';
  $: xeroConnected = xeroAuthenticated && xeroSettingsSaved;
  $: xeroFunctionsReady = jobtreadConnected && (xeroConnected || xeroNeedsFix);
  // Same rule as Layout: org members can open Direct Bills without their own Xero OAuth.
  $: directBillReady = jobtreadConnected && (isQuickBooks || xeroSettingsSaved);
  $: isAdmin = !!userInfo && (userInfo.id === 1 || userInfo.id === 2);

  function hasSavedXeroIntegrationSettings(info) {
    const invoiceCode = String(info?.['xeroinvoice-code'] || '').trim();
    const expenseCode = String(info?.['xeroexpense-code'] || '').trim();
    return Boolean(
      invoiceCode && invoiceCode !== 'BLANK' &&
      expenseCode && expenseCode !== 'BLANK'
    );
  }

  async function loadStatus() {
    try {
      const uRes = await fetch('/api/user');
      if (uRes.ok) {
        const uData = await uRes.json();
        if (uData.success && uData.user) {
          userInfo = uData.user;
          xeroSettingsSaved = hasSavedXeroIntegrationSettings(uData.user);
        }
      }
    } catch (err) {
      console.error('ModuleMenu: failed to load user info', err);
    }

    try {
      const jRes = await fetch('/api/jt/check-auth');
      const jData = await jRes.json();
      jobtreadConnected = jRes.ok && !!jData.authenticated;
    } catch {
      jobtreadConnected = false;
    }

    if (!isQuickBooks && jobtreadConnected) {
      try {
        const xRes = await fetch('/api/xero/check-auth');
        const xData = await xRes.json();
        xeroAuthenticated = xRes.ok && !!xData.authenticated;
        xeroNeedsFix = !xeroAuthenticated && isXeroReauthReason(xData.reason);
      } catch {
        xeroAuthenticated = false;
        xeroNeedsFix = false;
      }
    }

    loading = false;
  }

  onMount(loadStatus);

  async function handleLogout() {
    try {
      const r = await fetch('/api/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const d = await r.json();
      if (d.success) {
        settingsStore.resetSettings();
        user.set(null);
        navigate('/login');
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
  }

  // Build the module list reactively so cards enable/disable as status loads.
  $: modules = (() => {
    const list = [];

    if (!hideDirectBill) {
      list.push({
        title: 'Direct Bill Import',
        subtitle: 'One-step sync',
        badge: 'Preview',
        icon: 'upload',
        route: '/direct-bill-import',
        disabled: !directBillReady,
      });
    }

    if (!isQuickBooks) {
      list.push({
        title: 'Import Bills & Invoices',
        subtitle: 'JobTread to Xero',
        icon: 'link',
        route: '/import-from-jobtread',
        disabled: !xeroFunctionsReady,
      });
      list.push({
        title: 'Import Bills from Xero',
        subtitle: 'Xero to JobTread',
        icon: 'cloud-download',
        route: '/import-bills-from-xero',
        disabled: !xeroFunctionsReady,
      });
    }

    list.push({
      title: 'Cost Reporting',
      subtitle: 'Job cost report',
      icon: 'chart-bar',
      route: '/cost-reporting',
      disabled: !jobtreadConnected,
    });

    if (isAdmin) {
      list.push({ title: 'Sales CRM', subtitle: 'Admin', icon: 'users', route: '/crm', disabled: false });
      list.push({ title: 'Manage Users', subtitle: 'Admin', icon: 'users', route: '/manage-users', disabled: false });
    }

    return list;
  })();

  function open(mod) {
    if (mod.disabled) return;
    navigate(mod.route);
  }

  const chipBase = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium';
</script>

<!-- Connection status — mobile only (desktop shows this in the sidebar) -->
<div class="md:hidden mb-4 flex flex-wrap items-center gap-2">
  {#if !isQuickBooks}
    <span class="{chipBase} {xeroConnected ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : xeroNeedsFix ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200'}">
      <span class="w-1.5 h-1.5 rounded-full {xeroConnected ? 'bg-emerald-500' : xeroNeedsFix ? 'bg-amber-500' : 'bg-red-500'}"></span>
      Xero {xeroConnected ? 'Connected' : xeroNeedsFix ? 'needs fixing' : 'Not connected'}
    </span>
  {/if}
  <span class="{chipBase} {jobtreadConnected ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}">
    <span class="w-1.5 h-1.5 rounded-full {jobtreadConnected ? 'bg-emerald-500' : 'bg-red-500'}"></span>
    JobTread {jobtreadConnected ? 'Connected' : 'Not connected'}
  </span>
  {#if (!isQuickBooks && !xeroConnected) || !jobtreadConnected}
    <button on:click={() => navigate(xeroNeedsFix && jobtreadConnected ? '/settings/xero' : '/settings')} class="text-xs text-[#4B6A9B] underline">Fix in Settings</button>
  {/if}
</div>

<div class="grid grid-cols-1 gap-3">
  {#each modules as mod (mod.route)}
    <button
      type="button"
      on:click={() => open(mod)}
      disabled={mod.disabled}
      class="group relative flex items-center gap-3 sm:gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition-all
        {mod.disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:shadow-md hover:border-[#5B8AC4] active:scale-[0.99]'}"
    >
      <span class="flex-shrink-0 flex items-center justify-center h-11 w-11 rounded-lg bg-[#eef3fb] text-[#4B6A9B]">
        <Icon name={mod.icon} className="h-6 w-6" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="flex items-center gap-2">
          <span class="block text-[15px] font-semibold text-gray-900 truncate">{mod.title}</span>
          {#if mod.badge}
            <span class="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none bg-purple-500 text-white rounded">{mod.badge}</span>
          {/if}
        </span>
        <span class="block text-xs text-gray-500 mt-0.5">
          {mod.disabled ? 'Connect JobTread/Xero to enable' : mod.subtitle}
        </span>
      </span>
      {#if !mod.disabled}
        <svg class="flex-shrink-0 h-5 w-5 text-gray-300 group-hover:text-[#5B8AC4] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      {/if}
    </button>
  {/each}
</div>

{#if loading && modules.length === 0}
  <p class="text-sm text-gray-400 mt-2">Loading modules…</p>
{/if}

<!-- Settings / Help / Logout — mobile only (desktop has these in the sidebar) -->
<div class="md:hidden mt-6 pt-4 border-t border-gray-200 grid grid-cols-3 gap-3">
  <button
    type="button"
    on:click={() => navigate('/settings')}
    class="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white py-3 text-gray-700 shadow-sm active:scale-[0.98] transition-transform"
  >
    <Icon name="settings" className="h-5 w-5 text-[#4B6A9B]" />
    <span class="text-xs font-medium">Settings</span>
  </button>
  <button
    type="button"
    on:click={() => navigate('/help')}
    class="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white py-3 text-gray-700 shadow-sm active:scale-[0.98] transition-transform"
  >
    <Icon name="help" className="h-5 w-5 text-[#4B6A9B]" />
    <span class="text-xs font-medium">Help</span>
  </button>
  <button
    type="button"
    on:click={handleLogout}
    class="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white py-3 text-gray-700 shadow-sm active:scale-[0.98] transition-transform"
  >
    <Icon name="logout" className="h-5 w-5 text-[#4B6A9B]" />
    <span class="text-xs font-medium">Logout</span>
  </button>
</div>
